import React, { ReactNode, createContext, useCallback, useContext, useMemo, useState } from 'react';

import { Product, ProductInformationSettingsV2 } from 'apis/offer-listing-service/types';
import { isDefined } from 'common/utils';
import { useProductInformationSettings } from 'modules/shared-menu/hooks/useProductInformationSettings';
import { useSelectionData } from 'modules/shared-menu/hooks/useSelectionData';

import { ProductFormValues } from '../components/OfferDetailsForm/types';
import { useSelectionProducts } from '../hooks/useSelectionProducts';
import { Offer, ProductMaintainFields } from '../types';

type ProductOverridesContextData = {
  connectedProductId: string | undefined;
  setConnectedProductId: React.Dispatch<React.SetStateAction<string | undefined>>;
  currOverriddenFormFields: (keyof ProductFormValues)[];
  currOverriddenProductFields: ProductMaintainFields[];
  isAttrConnectedToProduct: (attr: keyof ProductFormValues) => boolean;
  overrideProductAttr: (...attrs: (keyof ProductFormValues)[]) => void;
  connectProductAttr: (attr: keyof ProductFormValues) => void;
  connectedProduct: Product | undefined;
  productInfoSettings: ProductInformationSettingsV2 | undefined;
  loadingConnectedProduct: boolean;
};

type ProductOverridesProviderProps = {
  children: ReactNode;
  offer?: Offer;
  productIds?: string[];
};

const ProductOverridesContext = createContext({} as ProductOverridesContextData);

export const ProductOverridesProvider = ({ children, offer, productIds }: ProductOverridesProviderProps) => {
  const { selection } = useSelectionData();
  const { selectionProducts, loadingSelectionProducts } = useSelectionProducts(productIds);
  const { productInfoSettings } = useProductInformationSettings({ selectionId: selection.id });
  const [connectedProductId, setConnectedProductId] = useState<string | undefined>(offer?.product_id);
  const [currOverriddenFormFields, setCurrOverriddenFormFields] = useState<(keyof ProductFormValues)[]>(() =>
    (offer?.product_maintain_fields || []).map(field => ProductFieldsToFeedFormValues[field]).filter(field => !!field),
  );
  const connectedProduct = useMemo(
    () => selectionProducts?.find(product => product && product.id === connectedProductId),
    [connectedProductId, selectionProducts],
  );

  const currOverriddenProductFields = useMemo(
    () => currOverriddenFormFields.map(field => FeedFormValuesToProductFields[field]).filter(isDefined),
    [currOverriddenFormFields],
  );

  const isAttrConnectedToProduct = useCallback(
    (attr: keyof ProductFormValues) => {
      if (connectedProductId) {
        return !currOverriddenFormFields.includes(attr);
      }
      return false;
    },
    [connectedProductId, currOverriddenFormFields],
  );

  const overrideProductAttr = useCallback(
    (...attrs: (keyof ProductFormValues)[]) => {
      setCurrOverriddenFormFields([...currOverriddenFormFields, ...attrs]);
    },
    [currOverriddenFormFields],
  );

  const connectProductAttr = useCallback(
    (attr: keyof ProductFormValues) => {
      setCurrOverriddenFormFields(currOverriddenFormFields.filter(field => field !== attr));
    },
    [currOverriddenFormFields],
  );

  return (
    <ProductOverridesContext.Provider
      value={{
        connectedProductId,
        setConnectedProductId,
        currOverriddenFormFields,
        currOverriddenProductFields,
        overrideProductAttr,
        connectProductAttr,
        isAttrConnectedToProduct,
        connectedProduct,
        productInfoSettings,
        loadingConnectedProduct: loadingSelectionProducts,
      }}
    >
      {children}
    </ProductOverridesContext.Provider>
  );
};

export const useProductOverridesContext = () => useContext(ProductOverridesContext);

/**
 * caffeine_content is not implemented in Product yet
 * deposit_id doesn't exist in Product
 */
const ProductFieldsToFeedFormValues: { [key in ProductMaintainFields]: keyof ProductFormValues } = {
  additives: 'additives',
  alcohol_permille: 'alcoholPercentage',
  allergens: 'allergens',
  brand_id: 'brandId',
  conditions_of_use_and_storage: 'usageInstructions',
  country_of_origin: 'countryOfOrigin',
  description: 'description',
  dietary_preferences: 'dietaryPreferences',
  display_unit: 'displayUnit',
  distributor_information: 'distributorInfo',
  images: 'image',
  ingredients: 'ingredients',
  mandatory_warnings: 'mandatoryWarnings',
  more_information: 'moreInformation',
  name: 'name',
  number_of_units: 'numberOfUnits',
  nutrition_facts: 'nutritionFacts',
  nutrition_values: 'nutritionValues',
  producer_information: 'producerInfo',
  volume_in_ml: 'volumeInMl',
  weight_in_grams: 'weightInGrams',
};

export const FeedFormValuesToProductFields: { [key in keyof Partial<ProductFormValues>]: ProductMaintainFields } = {
  additives: 'additives',
  alcoholPercentage: 'alcohol_permille',
  allergens: 'allergens',
  brandId: 'brand_id',
  countryOfOrigin: 'country_of_origin',
  description: 'description',
  displayUnit: 'display_unit',
  distributorInfo: 'distributor_information',
  image: 'images',
  ingredients: 'ingredients',
  mandatoryWarnings: 'mandatory_warnings',
  moreInformation: 'more_information',
  name: 'name',
  numberOfUnits: 'number_of_units',
  nutritionFacts: 'nutrition_facts',
  nutritionValues: 'nutrition_values',
  producerInfo: 'producer_information',
  usageInstructions: 'conditions_of_use_and_storage',
  volumeInMl: 'volume_in_ml',
  weightInGrams: 'weight_in_grams',
  dietaryPreferences: 'dietary_preferences',
};
